import { DEFAULT_CRYPTO_DECIMALS, ZERO_WEI } from '@kwenta/sdk/constants'
import { formatNumber, truncateNumbers } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import CloseIcon from 'assets/svg/app/close.svg'
import InfoIcon from 'assets/svg/app/info-white.svg'
import SnxLogo from 'assets/svg/partners/snx.svg'
import Button from 'components/Button'
import UnderlineTabs from 'components/Button/UnderlineTabs'
import DropdownField from 'components/DropdownField'
import Input from 'components/Input/Input'
import NumericInput from 'components/Input/NumericInput'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { type FC, memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DrawerTab, WithdrawTab, chainOptions, coinOptions } from 'sections/accounts/constants'
import { SectionHeading } from 'sections/shared/components/SectionHeading'
import { setOpenModal, setOpenModalWithParams } from 'state/app/reducer'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectWithdrawableFromIsolatedAccount } from 'state/futures/isolatedMargin/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import styled from 'styled-components'
import { resetButtonCSS } from 'styles/common'
import { isAddress } from 'viem'

type WithdrawPanelProps = {
	onDismiss: () => void
}

const WithdrawPanel: FC<WithdrawPanelProps> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const [amount, setAmount] = useState('')
	const [withdrawAddress, setWithdrawAddress] = useState('')
	const totalWithdrawable = useAppSelector(selectWithdrawableFromIsolatedAccount)

	const coinsList = useMemo(
		() => coinOptions.filter(({ providers }) => providers.includes(perpsProvider)),
		[perpsProvider]
	)
	const chainsList = useMemo(
		() => chainOptions.filter(({ providers }) => providers.includes(perpsProvider)),
		[perpsProvider]
	)

	const [selectedCoin, setSelectedCoin] = useState(coinsList[0].value)
	const [selectedChain, setSelectedChain] = useState(chainsList[0].value)

	const withdrawTabs = useMemo(
		() => [
			{
				name: WithdrawTab.ONCHAIN,
				label: t('account.withdraw-section.tabs.onchain-withdrawal'),
				active: true,
				onClick: (): void => {},
			},
			{
				name: WithdrawTab.INTERNAL,
				label: t('account.withdraw-section.tabs.internal-transfer'),
				active: false,
				disabled: true,
				onClick: (): void => {},
			},
		],
		[t]
	)

	const handleSetMax = useCallback(() => {
		setAmount(truncateNumbers(totalWithdrawable, DEFAULT_CRYPTO_DECIMALS))
	}, [totalWithdrawable])

	const showWalletDrawer = useCallback(() => dispatch(setOpenModal('manage_wallet')), [dispatch])

	return (
		<Container>
			<PanelContainer rowGap="25px">
				<FlexDivRowCentered>
					<SectionHeading title={t('account.withdraw-section.title')} marginTop={0} />
					<DismissButton onClick={onDismiss}>
						<CloseIcon width={12} height={12} />
					</DismissButton>
				</FlexDivRowCentered>
				<TabButtonsContainer>
					<UnderlineTabs options={withdrawTabs} noBorders={true} />
				</TabButtonsContainer>
				<FlexDivCol rowGap="20px">
					<DropdownField
						label={t('account.withdraw-section.coin')}
						options={coinsList}
						value={selectedCoin}
						onChange={setSelectedCoin}
					/>
					<FlexDivCol rowGap="10px">
						<Body color="secondary">{t('account.withdraw-section.wallet-address')}</Body>
						<InputContainer>
							<StyledInput
								placeholder="0x"
								value={withdrawAddress}
								onChange={(e) => setWithdrawAddress(e.target.value)}
							/>
						</InputContainer>
					</FlexDivCol>
					<DropdownField
						label={t('account.withdraw-section.chain')}
						options={chainsList}
						value={selectedChain}
						onChange={setSelectedChain}
					/>
					<FlexDivCol rowGap="10px">
						<FlexDivRow>
							<Body color="secondary">{t('account.withdraw-section.amount')}</Body>
							<Body color="secondary" onClick={handleSetMax}>
								{t('account.withdraw-section.balance')}
								{formatNumber(totalWithdrawable, { suggestDecimals: true, floor: true })}
							</Body>
						</FlexDivRow>
						<InputContainer>
							<StyledNumericInput
								placeholder="0.00"
								value={amount}
								onChange={(_, v) => setAmount(v)}
							/>
						</InputContainer>
					</FlexDivCol>
					<FlexDivCol rowGap="10px">
						<FlexDivRowCentered>
							<FlexDivRowCentered columnGap="5px">
								<Body color="secondary">{t('account.withdraw-section.withdraw-account')}</Body>
								<InfoIcon width={12} height={12} />
							</FlexDivRowCentered>
							<FlexDivRowCentered columnGap="5px">
								<SnxLogo /> <Body weight="bold">{t('account.withdraw-section.kwenta')}</Body>
							</FlexDivRowCentered>
						</FlexDivRowCentered>
						<FlexDivRowCentered>
							<FlexDivRowCentered columnGap="5px">
								<Body color="secondary">{t('account.withdraw-section.asset-balance')}</Body>
								<InfoIcon width={12} height={12} />
							</FlexDivRowCentered>
							<Body color="secondary">
								{formatNumber(totalWithdrawable, { suggestDecimals: true, floor: true })}{' '}
								{selectedCoin}
							</Body>
						</FlexDivRowCentered>
						<Divider height={1} divider />
						<FlexDivRowCentered>
							<Body color="secondary">{t('account.withdraw-section.network-fee')}</Body>
							<Body color="secondary">
								{formatNumber(ZERO_WEI)}
								{selectedCoin}
							</Body>
						</FlexDivRowCentered>
						<FlexDivRowCentered>
							<Body color="secondary">{t('account.withdraw-section.amount-received')}</Body>
							<Body color="primary" size="large" weight="bold">
								{amount === '' ? formatNumber(ZERO_WEI) : amount} {selectedCoin}
							</Body>
						</FlexDivRowCentered>
						<Divider height={1} divider />
					</FlexDivCol>
					<FlexDivCol rowGap="8px">
						<Body color="secondary">{t('account.withdraw-section.important')}</Body>
						<InfoContainer columnGap="4px">
							<InfoIconContainer>
								<InfoIcon width={14} height={14} />
							</InfoIconContainer>
							<Paragraph color="secondary">
								{t('account.withdraw-section.important-info')}
							</Paragraph>
						</InfoContainer>
					</FlexDivCol>
					<Paragraph color="secondary">{t('account.withdraw-section.copy')}</Paragraph>
				</FlexDivCol>
			</PanelContainer>
			<Footer>
				<Button size="small" fullWidth onClick={showWalletDrawer}>
					{t('account.withdraw-section.cancel-button')}
				</Button>
				<Button
					size="small"
					variant="solid-highlight"
					fullWidth
					disabled={
						amount === '' || wei(amount).gt(totalWithdrawable) || !isAddress(withdrawAddress)
					}
					onClick={() =>
						dispatch(
							setOpenModalWithParams({
								type: 'manage_wallet',
								params: {
									tab: DrawerTab.CONFIRM,
									amount,
									selectedCoin,
									selectedChain,
									withdrawAddress,
								},
							})
						)
					}
				>
					{t('account.withdraw-section.withdraw-button')}
				</Button>
			</Footer>
		</Container>
	)
})

const Container = styled(FlexDivCol)`
	position: relative;
	height: 100%;
	min-height: 400px;
	overflow-y: scroll;
`

const Paragraph = styled(Body)`
	line-height: 18px;
`

const InfoIconContainer = styled.div`
	width: 5%;
	margin-top: 2px;
`

const PanelContainer = styled(FlexDivCol)`
	padding: 20px;
	margin-bottom: 60px;
`

const DismissButton = styled.button`
	${resetButtonCSS};
	position: absolute;
	top: 25px;
	right: 20px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	&:hover {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const Footer = styled(FlexDivRowCentered)`
	position: fixed;
	bottom: 0;
	width: 100%;
	border-top: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	padding: 15px;
	column-gap: 15px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
`

const InfoContainer = styled(FlexDivRow)`
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.pill.yellowGray.background};
	border-radius: 8px;
	padding: 10px;
`

const InputContainer = styled.div`
	position: relative;
`

const StyledInput = styled(Input)`
	width: 100%;
`

const StyledNumericInput = styled(NumericInput)`
	width: 100%;
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;

	button {
		font-size: 13px;
	}

	> div > div {
		padding-left: 0px;
	}

	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`

const Divider = styled(Spacer)`
	width: 100%;
`

export default WithdrawPanel
